// Layer 2: Picto Size and Configuration Layer
// Determines picto size and prepares configuration based on resource type and positioning mode

import { IResourcePosition } from './resourcePositionLayer';
import { IScaleParams } from '../../types';
import { scalePictoSize, calculateGridSquareSize, calculateCenteringOffset, calculatePinOffset } from '../pictoSize';
import { ARBITRARY_PICTO_SIZE } from '../constants';

export interface IResourceWithSize extends IResourcePosition {
  size: number;
  mapIconSize: number;
  resourceTypeId?: number;
}

export interface IPictoConfiguration extends IResourceWithSize {
  finalX: number;
  finalY: number;
}

export enum PictoSizeMode {
  /** Size based on resource type mapIconSize */
  ResourceBased = 'resource-based',
  /** Fixed size for bookings/users */
  Fixed = 'fixed',
}

export enum PictoPositioningMode {
  /** Standard centering within grid cell */
  Center = 'center',
  /** Pin-style with tip at center (for location dots) */
  Pin = 'pin',
}

/**
 * Layer 2: Picto Size and Configuration Layer
 *
 * Calculates final picto sizes and pixel positions based on:
 * - Resource type (for resource pictos)
 * - Fixed size (for booking/user pictos)
 * - Positioning mode (center vs pin)
 */
export class PictoSizeLayer {
  /**
   * Applies size to resource positions based on resource type
   */
  static applySizes(
    positions: IResourcePosition[],
    resourceIconSizes: Map<number, number | undefined>, // resourceId -> mapIconSize
    scaleParams: IScaleParams,
    mode: PictoSizeMode = PictoSizeMode.ResourceBased,
    mapIconScale = 3
  ): IResourceWithSize[] {
    return positions.map((pos) => {
      const mapIconSize = resourceIconSizes.get(pos.id) ?? 1;

      let size: number;
      if (mode === PictoSizeMode.Fixed) {
        size = ARBITRARY_PICTO_SIZE;
      } else {
        size = scalePictoSize(mapIconSize * mapIconScale, scaleParams.naturalWidth, false);
      }

      return {
        ...pos,
        size,
        mapIconSize,
      };
    });
  }

  /**
   * Calculates final pixel positions based on grid or API coordinates
   */
  static calculateFinalPositions(
    resourcesWithSize: IResourceWithSize[],
    scaleParams: IScaleParams,
    positioningMode: PictoPositioningMode = PictoPositioningMode.Center
  ): IPictoConfiguration[] {
    const gridSquareSize = calculateGridSquareSize(scaleParams);

    return resourcesWithSize.map((resource) => {
      let finalX: number;
      let finalY: number;

      if (resource.isGridPosition) {
        // Convert grid coordinates to pixels
        const offset =
          positioningMode === PictoPositioningMode.Pin
            ? calculatePinOffset(gridSquareSize)
            : calculateCenteringOffset(resource.size, gridSquareSize);

        finalX = offset.tx + resource.x * gridSquareSize;
        finalY = offset.ty + resource.y * gridSquareSize;
      } else {
        // Already pixel coordinates from API - just center the picto
        finalX = resource.x - resource.size / 2;
        finalY = resource.y - resource.size / 2;
      }

      return {
        ...resource,
        finalX,
        finalY,
      };
    });
  }

  /**
   * Complete pipeline: positions -> sizes -> final pixel positions
   */
  static process(
    positions: IResourcePosition[],
    resourceIconSizes: Map<number, number | undefined>,
    scaleParams: IScaleParams,
    sizeMode: PictoSizeMode = PictoSizeMode.ResourceBased,
    positioningMode: PictoPositioningMode = PictoPositioningMode.Center,
    mapIconScale = 3
  ): IPictoConfiguration[] {
    const withSizes = this.applySizes(positions, resourceIconSizes, scaleParams, sizeMode, mapIconScale);

    return this.calculateFinalPositions(withSizes, scaleParams, positioningMode);
  }
}
